import { Building2, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';
import { useNavigate } from 'react-router-dom';

export default function SwitchOrganization() {
  const navigate = useNavigate();
  const { activeOrg, switchOrganization, getUserOrganizations } = useAuth();
  const userOrgs = getUserOrganizations();

  const handleSwitch = (orgId) => {
    switchOrganization(orgId);
    navigate('/');
    window.location.reload();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'org_admin': return 'bg-primary text-primary-foreground';
      case 'planner': return 'bg-accent text-accent-foreground';
      case 'read_only_client': return 'bg-secondary text-secondary-foreground';
      default: return 'bg-muted';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'org_admin': return 'Org Admin';
      case 'planner': return 'Planner';
      case 'read_only_client': return 'Client';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen gradient-soft pb-12">
      <EnhancedAppHeader 
        title="Switch Organization"
        subtitle="Select which organization to work with"
      />

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {userOrgs.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Organizations</h2>
              <p className="text-muted-foreground mb-6">
                You don't belong to any organizations yet
              </p>
              <Button onClick={() => navigate('/setup-organization')} className="gradient-primary">
                Create Organization
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {userOrgs.map((org) => (
                <Card 
                  key={org.id} 
                  className={`p-6 transition-all hover:shadow-medium ${
                    org.id === activeOrg.id ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="p-3 rounded-lg bg-primary/10 shrink-0">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold truncate">{org.name}</h3>
                          {org.id === activeOrg.id && (
                            <Badge className="bg-success text-primary-foreground">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </div>
                        <Badge className={getRoleBadgeColor(org.role)}>
                          {getRoleLabel(org.role)}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-2">
                          Slug: {org.slug}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {org.id === activeOrg.id ? (
                        <Button variant="outline" disabled>
                          Current
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleSwitch(org.id)}
                          className="gradient-primary"
                        >
                          Select
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}